import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  DatePicker,
  FileIcon,
  InputNumber,
  QuestionFillIcon,
  Radio,
  Select,
  Text,
  Tooltip,
} from '@mercai/clever'

import { getDateWithWeekdayOffset } from '../../helpers'
import { validateFields } from './order-data.helper'

import {
  QuotationDeliveryEnum,
  QuotationProps,
  TransportCompanyProps,
} from '@/types'
import { SectionHeader } from '../section-header'
import styles from './order-data.module.css'
import { OrderDataProps } from './order-data.type'

export const OrderData = ({
  position,
  isComplete,
  quotationValues,
  setQuotationValues,
  errors,
  getMetrics,
}: OrderDataProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenBusinessV2')

  const onHandleValues = (
    key: string,
    value: number | string | boolean | undefined | TransportCompanyProps,
  ) => {
    const data = {
      ...quotationValues,
      [key]: value,
      ...(key === 'logisticTransportCompany' && {
        logisticTransportCompanyUuid: (value as TransportCompanyProps)?.uuid,
      }),
    }

    setQuotationValues(data)

    if (key === 'deliveryOption' || key === 'includeIva') {
      getMetrics(data as QuotationProps)
    }
  }

  useEffect(() => {
    if (!quotationValues?.expiresAt) {
      const dateAdded3Days = getDateWithWeekdayOffset()
      setQuotationValues((prev) => ({ ...prev, expiresAt: dateAdded3Days }))
    }
  }, [quotationValues])

  return (
    <SectionHeader
      title={`${position}. ${t('orderDataTitle')}`}
      key="order-data"
      isComplete={isComplete}
    >
      <div className={styles.core}>
        <div className={styles['info-block']}>
          <div className={styles.title}>
            <FileIcon className={styles.icon} />
            <Text variant="title-medium">{t('orderDataGeneralInfoTitle')}</Text>
          </div>

          <div className={styles['info-block-content']}>
            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataDueDateLabel')} <span>*</span>
              </Text>
              <DatePicker
                placeholder={t('orderDataDueDatePlaceholder')}
                hasTime
                value={quotationValues?.expiresAt}
                onChange={(newValue) => {
                  onHandleValues('expiresAt', newValue)
                }}
                error={errors?.expiresAt as string}
                onBlur={() => {
                  getMetrics()
                }}
              />
              {validateFields.expiresAt(
                quotationValues?.expiresAt || '',
                t,
                styles,
              )}
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataPostDeliveryDaysLabel')} <span>*</span>
              </Text>
              <InputNumber
                suffix={t('orderDataPostDeliveryDaysSufixLabel')}
                placeholder={t('orderDataPostDeliveryDaysPlaceholder')}
                value={quotationValues?.expectedDeliveryDays}
                error={errors?.expectedDeliveryDays as string}
                decimalPrecision={0}
                min={0}
                stepSize={1}
                showIcons
                onChange={(newValue) => {
                  onHandleValues('expectedDeliveryDays', newValue)
                }}
                onBlur={() => {
                  getMetrics()
                }}
              />
              {validateFields.expectedDeliveryDays(
                quotationValues?.expectedDeliveryDays || 0,
                t,
                styles,
              )}
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">{t('orderDataDiscountLabel')}</Text>
              <InputNumber
                prefix="$ "
                placeholder={t('orderDataDiscountPlaceholder')}
                value={
                  quotationValues?.discount ? +quotationValues.discount : 0
                }
                error={errors?.discount as string}
                stepSize={100}
                showIcons
                decimalPrecision={2}
                onChange={(newValue) => onHandleValues('discount', newValue)}
                onBlur={() => getMetrics()}
              />
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataShippingToBuyerLabel')} <span>*</span>
              </Text>
              <InputNumber
                prefix="$ "
                placeholder={t('orderDataShippingToBuyerPlaceholder')}
                value={quotationValues?.totalFreightPrice || 0}
                error={errors?.totalFreightPrice as string}
                min={0}
                stepSize={100}
                showIcons
                decimalPrecision={2}
                onChange={(newValue) =>
                  onHandleValues('totalFreightPrice', newValue)
                }
                onBlur={() => getMetrics()}
              />
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataDeliveryOptionLabel')}
              </Text>
              <Select
                value={quotationValues?.deliveryOption}
                options={[
                  {
                    label: t('orderDataDeliveryOptionEmptyLabel'),
                    value: '',
                  },
                  ...Object.keys(QuotationDeliveryEnum).map((item) => ({
                    label: tEnums(`logistic_shipment_delivery_option_${item}`),
                    value: item,
                  })),
                ]}
                onChange={(value) => {
                  onHandleValues('deliveryOption', value)
                }}
                placeholder={t('orderDataDeliveryOptionPlaceholder')}
                error={errors?.deliveryOption as string}
              />
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataIncludeVATLabel')}
                <Tooltip
                  text={t('orderDataIncludeVATDescription')}
                  widthText="15rem"
                  orientation="right"
                >
                  <QuestionFillIcon />
                </Tooltip>
              </Text>
              <div className={styles['radio-group']}>
                <Radio
                  value={!!quotationValues?.includeIva}
                  onChange={() => {
                    onHandleValues('includeIva', true)
                  }}
                >
                  {t('yesLabel')}
                </Radio>

                <Radio
                  value={!quotationValues?.includeIva}
                  onChange={() => {
                    onHandleValues('includeIva', false)
                  }}
                >
                  {t('noLabel')}
                </Radio>
              </div>
            </div>

            <div className={styles['info-block-content-item']}>
              <Text variant="text-base">
                {t('orderDataDisableNotificationLabel')}
                <Tooltip
                  text={t('orderDataDisableNotificationDescription')}
                  widthText="15rem"
                  orientation="right"
                >
                  <QuestionFillIcon />
                </Tooltip>
              </Text>
              <div className={styles['radio-group']}>
                <Radio
                  value={!!quotationValues?.disableNotifications}
                  onChange={() => {
                    onHandleValues(
                      'disableNotifications',
                      !quotationValues?.disableNotifications,
                    )
                  }}
                >
                  {t('yesLabel')}
                </Radio>

                <Radio
                  value={!quotationValues?.disableNotifications}
                  onChange={() => {
                    onHandleValues(
                      'disableNotifications',
                      !quotationValues?.disableNotifications,
                    )
                  }}
                >
                  {t('noLabel')}
                </Radio>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionHeader>
  )
}
